<template>
  <div class="form-ttl essen">
    <!--필수값: essen-->
    <span>휴대전화 문자 인증</span>
  </div>
  <div class="form-notice">국가번호를 선택 후 휴대전화 번호를 입력해주세요.</div>
  <div class="form-in " :class="{ timer: phoneAuthActive }">
    <label class="label widthFit">
      <!--옵션에서는 전체 문자가 다 보여지지만 선택된 영역에서는 퍼블 레이아웃 이슈로 국가명 제외한 '+82' 만 노출되어야 합니다-->
      <select v-model="modelValue.code" :disabled="phoneAuthActive || phoneAuthComplete">
        <option selected :value="'+82'">+82</option>
        <option :value="'+1'">+1</option>
      </select>
    </label>

    <label class="label">
      <input
        type="number"
        :placeholder="placeholder || '' - ' 제외 전화번호를 입력해 주세요.'"
        :disabled="phoneAuthActive || phoneAuthComplete"
        :value="modelValue.phone"
        @input="onInput"
        inputmode="numeric"
        pattern="[0-9]*"
      />
      <!--placeholder="'-' 제외 전화번호를 입력해 주세요."-->
      <!--@input="emits('update:modelValue', $event.target.value)"-->
      <span class="time" v-if="phoneAuthActive">{{ timer }}</span>
    </label>

    <button
      @click="requestAuth"
      class="btn border-primary-dark-100 regular"
      :class="phoneAuthComplete ? 'disabled' : ''"
      :disabled="phoneAuthComplete"
    >
      {{ phoneAuthActive ? '재발송' : phoneAuthComplete ? '완료' : '발송' }}
    </button>
  </div>

  <Validation :object-valid="validation" />

  <div class="form-in" v-if="phoneAuthActive">
    <label class="label">
      <input
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        autocomplete="one-time-code"
        placeholder="인증번호 6자리"
        v-model="state.phoneCode"
        @input="onCodeInput"
        maxlength="6"
      />
    </label>
    <button class="btn border-primary-dark-100 regular" @click="checkAuthCode">인증</button>
  </div>

  <Validation :custom-valid="props.customValid.text" />
  <!-- MEMO: 운영 배포 시 지우기 -->
  <!--  <div v-if="ConstCode.MODE !== 'production'">{{ authCode }}</div>-->
</template>
<script setup>
import {
  AUTHENTICATION_STATUS,
  doAuth,
  finishAuth,
  initialize,
  phoneState,
  timer
} from '@/common/utils/module/AuthenticateSupport';
import InputComponent from '@/components/common/input/InputComponent.vue';
import Validation from '@/components/common/layouts/Validation.vue';
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import store from '@/store';
import { debounce } from 'lodash';
import { validPropsTouch } from '@/common/validation';
import { isEmpty } from '@/common/utils/Util';
import { toastAlert } from '@/common/utils/module/ToastSupport';
import ConstCode from '@/common/constants/ConstCode';
import { showTextModal } from '@/common/utils/ModalState';
import { nativeStartSmsListener } from '@/common/utils/module/NativeRequest';
import { isApp } from '@/common/utils/module/IsMobileSupport';

const emits = defineEmits(['update:modelValue']);
onBeforeMount(() => {
  store.commit('user/setPhone', props.modelValue);

  props.authInitCall();
});
// 260710 P0: 인증번호는 더 이상 클라에 내려오지 않음(서버 대조) — getAuthCode 클라 비교 폐지.
// 인증이 성공했는지 체크
const phoneAuthComplete = computed(() => phoneState.value === AUTHENTICATION_STATUS.COMPLETED);
// 인증이 진행중인지 체크
const phoneAuthActive = computed(
  () => phoneState.value === AUTHENTICATION_STATUS.TIME_OUT || phoneState.value === AUTHENTICATION_STATUS.ON_GOING
);

const state = reactive({
  phoneCode: '' // 사용자가 입력한 인증번호
});
const props = defineProps({
  placeholder: {
    type: String,
    default: '숫자만 입력해주세요.'
  },
  modelValue: {
    type: [String, Number, Object],
    default: ''
  },
  validation: {
    type: Object,
    default: null
  },
  customValid: {
    type: Object,
    default: null
  },
  // 어떤 휴대폰인증 API를 사용할것인지
  authInitCall: {
    type: Function,
    default: () => {
      initialize('03:00', async () => {
        return await store.dispatch('user/getAuthCode');
      });
    }
  },
  errorText: {
    type: String,
    default: ''
  }
});

// 인증에대한 유효성 처리 — 260710 P0: 서버 검증(user/verifyPhoneCode)로 대체. 클라 코드 비교 폐지.
const checkAuthCode = async () => {
  if (phoneState.value === AUTHENTICATION_STATUS.TIME_OUT) {
    setPhoneCodeValidText('인증번호의 유효기간이 만료되었습니다. 인증번호를 재발송 해주세요.');
    return;
  }
  if (isEmpty(state.phoneCode)) {
    setPhoneCodeValidText('전화 번호 인증에 실패했습니다. 다시 시도해 주세요.');
    return;
  }
  const ok = await store.dispatch('user/verifyPhoneCode', state.phoneCode);
  if (!ok) {
    setPhoneCodeValidText('전화 번호 인증에 실패했습니다. 다시 시도해 주세요.');
    return;
  }
  await finishAuth(true);
  setPhoneCodeValidText('');
};

// 유효성 메시지 입력을 줄이기위해 함수선언
const setPhoneCodeValidText = text => {
  props.customValid.text = text;
};

// 인증번호 발송
const requestAuth = debounce(async () => {
  setPhoneCodeValidText('');
  if (!validPropsTouch(props.validation) || phoneAuthComplete.value) return;
  store.commit('user/setPhone', props.modelValue);
  const result = await doAuth();
  if (result === true) {
    // SMS 자동 읽기 리스너 시작 (앱 환경에서만)
    if (isApp()) {
      window.smsCodeCallback = (code) => {
        if (code && code.length === 6) {
          state.phoneCode = code;
          // 자동 인증 실행
          checkAuthCode();
        }
      };
      nativeStartSmsListener('smsCodeCallback');
    }
  } else {
    setPhoneCodeValidText(props.errorText || '인증번호 발송에 실패하였습니다.');
    showTextModal(props.errorText || result || '이미 등록된 휴대전화 번호입니다.', '확인', () =>
      setPhoneCodeValidText('')
    );
  }
}, 400);

const onCodeInput = (e) => {
  // 숫자만 허용
  state.phoneCode = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
  e.target.value = state.phoneCode;
  setPhoneCodeValidText('');
  // 6자리 입력되면 자동 인증
  if (state.phoneCode.length === 6) {
    checkAuthCode();
  }
};

const onInput = e => {
  let val = e.target.value;

  // 빈 문자열이면 그대로 유지
  if (val === '') {
    state.phone = '';
    return;
  }

  // 문자열로 변환하여 처리
  val = String(val).replace(/[^0-9]/g, '');

  if (val.length > 11) {
    val = val.slice(0, 11);
  }

  props.modelValue.phone = val;
  // DOM 값을 직접 업데이트
  e.target.value = val;
};
</script>
<style scoped></style>
